import { createFileRoute, Link } from '@tanstack/react-router'
import { useState, useEffect, useMemo } from 'react'
import { Users, Briefcase, UserCheck, GitMerge, ArrowRight, Plus } from 'lucide-react'
import { ProtectedRoute } from '../../components/ProtectedRoute'
import { useAuth } from '../../hooks/useAuth'
import { can } from '../../lib/roles'
import { DEFAULT_COMPANY_ID } from '../../lib/config'
import { listJobs, listCandidates, type JobDto } from '../../lib/recruitment'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/rekrut/')({
  component: RekrutIndexPage,
})

function RekrutIndexPage() {
  return (
    <ProtectedRoute minimumRole="manager">
      <RekrutIndexContent />
    </ProtectedRoute>
  )
}

const STATUS_VARIANT: Record<JobDto['status'], 'default' | 'success' | 'muted'> = {
  published: 'success',
  draft: 'muted',
  closed: 'muted',
}

const SECTIONS = [
  {
    to: '/rekrut/jobs/',
    label: 'Job Postings',
    description: 'Create and manage open positions.',
    icon: Briefcase,
  },
  {
    to: '/rekrut/candidates/',
    label: 'Candidates',
    description: 'Browse and add candidate profiles.',
    icon: UserCheck,
  },
  {
    to: '/rekrut/pipeline/',
    label: 'Pipeline',
    description: 'Kanban board — track all applications.',
    icon: GitMerge,
  },
] as const

function RekrutIndexContent() {
  const { user } = useAuth()
  const companyId = user?.companyId ?? DEFAULT_COMPANY_ID
  const canManage = can(user?.role, 'manage:rekrut')

  const [jobs, setJobs] = useState<JobDto[]>([])
  const [jobsLoading, setJobsLoading] = useState(true)
  const [candidateCount, setCandidateCount] = useState<number | null>(null)

  useEffect(() => {
    let cancelled = false
    listJobs(companyId).then((res) => {
      if (cancelled) return
      setJobsLoading(false)
      if (res.success && res.data) setJobs(res.data.jobs)
    })
    return () => { cancelled = true }
  }, [companyId])

  useEffect(() => {
    let cancelled = false
    listCandidates(companyId).then((res) => {
      if (cancelled) return
      if (res.success && res.data) setCandidateCount(res.data.candidates.length)
    })
    return () => { cancelled = true }
  }, [companyId])

  const published = useMemo(() => jobs.filter((j) => j.status === 'published').length, [jobs])
  const draft = useMemo(() => jobs.filter((j) => j.status === 'draft').length, [jobs])

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-50">
            <Users className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Rekrut</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Recruitment pipeline — jobs, candidates and hiring</p>
          </div>
        </div>
        {canManage && (
          <Link to="/rekrut/jobs/">
            <Button size="sm">
              <Plus className="w-4 h-4" />
              Post Job
            </Button>
          </Link>
        )}
      </div>

      {/* KPI summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="text-center">
          <CardContent className="pt-5 pb-4">
            <p className="text-2xl font-bold text-foreground">{jobsLoading ? '—' : jobs.length}</p>
            <p className="text-xs text-muted-foreground mt-1">Total Jobs</p>
          </CardContent>
        </Card>
        <Card className="text-center">
          <CardContent className="pt-5 pb-4">
            <p className="text-2xl font-bold text-green-600">{jobsLoading ? '—' : published}</p>
            <p className="text-xs text-muted-foreground mt-1">Published</p>
          </CardContent>
        </Card>
        <Card className="text-center">
          <CardContent className="pt-5 pb-4">
            <p className="text-2xl font-bold text-muted-foreground">{jobsLoading ? '—' : draft}</p>
            <p className="text-xs text-muted-foreground mt-1">Draft</p>
          </CardContent>
        </Card>
        <Card className="text-center">
          <CardContent className="pt-5 pb-4">
            <p className="text-2xl font-bold text-amber-600">{candidateCount ?? '—'}</p>
            <p className="text-xs text-muted-foreground mt-1">Candidates</p>
          </CardContent>
        </Card>
      </div>

      {/* Section nav cards */}
      <div>
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Sections</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {SECTIONS.map(({ to, label, description, icon: Icon }) => (
            <Link key={to} to={to}>
              <Card className="h-full hover:shadow-md transition-shadow group cursor-pointer border-border">
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between">
                    <div className="p-2 rounded-md bg-amber-50 mb-3">
                      <Icon className="w-4 h-4 text-amber-600" />
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-amber-600 group-hover:translate-x-0.5 transition-all mt-1" />
                  </div>
                  <CardTitle className="text-sm font-semibold group-hover:text-amber-700 transition-colors">
                    {label}
                  </CardTitle>
                  <CardDescription className="text-xs leading-relaxed">
                    {description}
                  </CardDescription>
                </CardHeader>
              </Card>
            </Link>
          ))}
        </div>
      </div>

      {/* Recent jobs */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Recent Job Postings</p>
          <Link to="/rekrut/jobs/" className="text-xs text-primary hover:underline">
            View all
          </Link>
        </div>
        {jobsLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-14 bg-muted rounded-lg animate-pulse" />
            ))}
          </div>
        ) : jobs.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center">
              <Briefcase className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No jobs posted yet.</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wide">Title</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wide hidden sm:table-cell">Department</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wide">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {jobs.slice(0, 5).map((job) => (
                  <tr key={job.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3 font-medium text-foreground">{job.title}</td>
                    <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">{job.department}</td>
                    <td className="px-4 py-3">
                      <Badge variant={STATUS_VARIANT[job.status]} className={cn('text-[10px] capitalize')}>
                        {job.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to="/rekrut/jobs/$jobId/"
                        params={{ jobId: job.id }}
                        className="text-xs text-primary hover:underline"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </div>
  )
}
